import React from 'react'
import { Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PackingItem } from '../../types'
import styles from './PackingItemsDisplay.module.css'

const { Text } = Typography

interface PackingItemsDisplayProps {
  items: PackingItem[]
  title?: string
  showTotal?: boolean
  colorDescription?: string
}

interface GroupedPackingItem extends PackingItem {
  key: string
  colorDescription?: string
}

const PackingItemsDisplay: React.FC<PackingItemsDisplayProps> = ({
  items,
  title = '装箱明细',
  showTotal = true,
  colorDescription
}) => {
  // 按尺码分组并合并相同条码的数量
  const groupedItems = React.useMemo(() => {
    const itemMap = new Map<string, PackingItem>()
    
    items.forEach(item => {
      // 使用 barcode 字段，如果没有则回退到 skuBarcode
      const barcode = (item as any).barcode || item.skuBarcode
      const key = `${barcode}-${item.size}`
      const existing = itemMap.get(key)
      
      if (existing) {
        existing.quantity += item.quantity
      } else {
        itemMap.set(key, { 
          ...item, 
          barcode: barcode
        })
      }
    })
    
    return Array.from(itemMap.values()).map((item, index) => ({
      ...item,
      key: `${(item as any).barcode || item.skuBarcode}-${item.size}-${index}`,
      colorDescription
    }))
  }, [items, colorDescription])

  // 计算总数量
  const totalQuantity = React.useMemo(() => {
    return groupedItems.reduce((sum, item) => sum + item.quantity, 0)
  }, [groupedItems])

  const columns: ColumnsType<GroupedPackingItem> = [
    {
      title: '颜色码',
      dataIndex: 'colorCode',
      key: 'colorCode',
      width: 90,
      render: (colorCode: string) => (
        <Tag color="blue">{colorCode}</Tag>
      )
    },
    {
      title: '颜色',
      dataIndex: 'colorDescription',
      key: 'colorDescription',
      width: 120,
      render: (description: string) => (
        <Text>{description || '-'}</Text>
      )
    },
    {
      title: '尺码',
      dataIndex: 'size',
      key: 'size',
      width: 70,
      align: 'center',
      render: (size: string) => (
        <Tag color="green">{size}</Tag>
      )
    },
    {
      title: '条码',
      key: 'barcode',
      width: 130,
      render: (record: GroupedPackingItem) => {
        const barcode = (record as any).barcode || record.skuBarcode
        
        // 如果没有条码，显示 "-"
        if (!barcode) {
          return <Text style={{ color: '#999' }}>-</Text>
        }
        
        return (
          <Text 
            code 
            copyable={{ text: barcode }}
            style={{ fontSize: '13px', fontFamily: 'monospace' }}
          >
            {barcode}
          </Text>
        )
      }
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 70,
      align: 'center',
      render: (quantity: number) => (
        <Text strong style={{ color: '#1890ff' }}>
          {quantity}
        </Text>
      )
    }
  ]

  if (groupedItems.length === 0) {
    return (
      <div className={styles.emptyState}>
        暂无装箱明细
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text strong>{title}</Text>
        {showTotal && (
          <Text type="secondary">
            共 {groupedItems.length} 个SKU，总数量: {totalQuantity} 件
          </Text>
        )}
      </div>
      
      <Table
        columns={columns}
        dataSource={groupedItems}
        pagination={false}
        size="small"
        bordered
        scroll={{ x: 480 }}
        className={styles.packingTable}
        summary={() => (
          showTotal ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4}>
                <Text strong>合计</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="center">
                <Text strong style={{ color: '#1890ff' }}>
                  {totalQuantity}
                </Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          ) : null
        )}
      />
    </div>
  )
}

export default PackingItemsDisplay